import mixitup from 'mixitup';

/**
 * Tabs A-1 block functionality.
 *
 * - Desktop: Sidebar tabs with MixItUp filtering
 * - Mobile: Accordion (handled by showAccordion module via separate markup)
 * - Supports ?section=N query parameter to open a specific tab/accordion on load
 *
 * @function tabsA1
 * @exports tabsA1
 * @module tabsA1
 * @see {@link https://www.kunkalabs.com/mixitup/}
 * @requires mixitup
 */

/**
 * Returns the combined height of sticky top elements (header + WP admin bar).
 *
 * @return {number}
 */
function getTopOffset() {
	let offset = 0;
	const header = document.querySelector('.js-header');
	const wpAdminBar = document.getElementById('wpadminbar');
	if (header) {
		offset += header.offsetHeight;
	}
	if (wpAdminBar) {
		offset += wpAdminBar.offsetHeight;
	}
	return offset;
}

/**
 * Scrolls smoothly to a target element, accounting for sticky header offset.
 *
 * @param {HTMLElement} element - The element to scroll to.
 */
function scrollToElement(element) {
	const targetPosition =
		element.getBoundingClientRect().top +
		window.scrollY -
		getTopOffset() -
		20;

	window.scrollTo({
		top: targetPosition,
		behavior: 'smooth',
	});
}

function blockInit(block) {
	const container = block.querySelector('.tabs-container');
	const buttons = Array.from(
		block.querySelectorAll('.o-tabs-a-1__tab-button'),
	);

	if (!container || buttons.length === 0) {
		return;
	}

	// Read ?section=N query param (1-indexed)
	const urlParams = new URLSearchParams(window.location.search);
	const sectionParam = parseInt(urlParams.get('section'), 10);
	const hasSectionParam =
		!isNaN(sectionParam) &&
		sectionParam >= 1 &&
		sectionParam <= buttons.length;

	let initialIndex = 0;
	if (hasSectionParam) {
		initialIndex = sectionParam - 1;
	}

	// Set initial active button based on query param or default (first)
	buttons.forEach(btn =>
		btn.classList.remove('o-tabs-a-1__tab-button--active'),
	);
	buttons[initialIndex].classList.add('o-tabs-a-1__tab-button--active');

	const defaultFilter =
		buttons[initialIndex]?.getAttribute('data-filter') || 'none';

	// Animation duration in milliseconds
	const animDuration = 200;

	// Initialize MixItUp on the content container
	const mixer = mixitup(container, {
		animation: {
			duration: animDuration,
			effects: 'fade',
			easing: 'ease',
		},
		controls: { enable: false },
		load: { filter: defaultFilter },
	});

	/**
	 * Updates the URL query parameter to reflect the active tab.
	 *
	 * @param {number} index - The 1-indexed tab number.
	 */
	function updateURLParam(index) {
		const url = new URL(window.location);
		url.searchParams.set('section', index);
		history.replaceState(null, '', url);
	}

	/**
	 * Handles tab button clicks, updating the active state, filtering content, and updating URL.
	 *
	 * @param {Event} event - The click event on a tab button.
	 */
	function handleTabClick(event) {
		const target = event.target.closest('.filter');
		if (!target) {
			return;
		}

		const filter = target.getAttribute('data-filter');

		// Update active button state
		buttons.forEach(btn =>
			btn.classList.remove('o-tabs-a-1__tab-button--active'),
		);
		target.classList.add('o-tabs-a-1__tab-button--active');

		// Apply the filter
		mixer.filter('none').then(() => mixer.filter(filter));

		// Update URL
		const newIndex = buttons.indexOf(target) + 1;
		updateURLParam(newIndex);
	}

	// Attach click handler to the sidebar
	const sidebar = block.querySelector('.o-tabs-a-1__sidebar');
	if (sidebar) {
		sidebar.addEventListener('click', handleTabClick);
	}

	// If ?section= param is present, scroll to block (desktop) or open accordion (mobile)
	if (hasSectionParam) {
		handleSectionParamScroll(block, initialIndex);
	}
}

/**
 * Handles the ?section= param on page load: scrolls to the tabs block on desktop,
 * or opens the corresponding accordion item and scrolls to it on mobile.
 * Scoped to this block only via o-tabs-a-1__accordion-wrapper selector.
 *
 * @param {HTMLElement} block - The block wrapper element (data-module="tabsA1").
 * @param {number} initialIndex - The 0-indexed tab/accordion item to target.
 */
function handleSectionParamScroll(block, initialIndex) {
	// Find the parent <section> element (the block outer wrapper)
	const blockSection = block.closest('section');
	if (!blockSection) {
		return;
	}

	// Find the accordion wrapper scoped to this specific tabs-a block
	const accordionWrapper = blockSection.querySelector(
		'.o-tabs-a-1__accordion-wrapper',
	);

	// Wait for DOM and layouts to settle
	setTimeout(() => {
		// Check if accordion is visible (mobile viewport)
		if (accordionWrapper && accordionWrapper.offsetParent !== null) {
			// MOBILE: Open the target accordion item and scroll to it
			const accordionGroups = Array.from(
				accordionWrapper.querySelectorAll('.m-accordion__group'),
			);
			const targetGroup = accordionGroups[initialIndex];

			if (targetGroup) {
				const trigger = targetGroup.querySelector('.js-accordion');
				if (trigger) {
					trigger.click();
				}
				scrollToElement(targetGroup);
			}
		} else {
			// DESKTOP: Scroll to the tabs block
			scrollToElement(blockSection);
		}
	}, 500);
}

export default function tabsA1() {
	const blocks = document.querySelectorAll("[data-module='tabsA1']");
	blocks.forEach(block => blockInit(block));
}
